import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, FileText, Send, Download, Trash2, Eye, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-700'
};

export default function Quotations() {
  const { API, user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, quotation: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const isAdmin = hasRole(['admin']);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [{ description: '', quantity: 1, unit: 'Nos', rate: 0 }],
    validity_days: 30,
    terms_and_conditions: '',
    notes: ''
  });

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const [main, lcos] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/customers`, { params: { customer_type: 'lco' } }),
      ]);
      const seen = new Set();
      setCustomers([...main.data, ...lcos.data].filter(c => !seen.has(c.customer_id) && seen.add(c.customer_id)));
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  useEffect(() => {
    fetchQuotations();
    fetchCustomers();
  }, [API]);

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit: 'Nos', rate: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const gst = subtotal * 0.18;
    return { subtotal, gst, total: subtotal + gst };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (formData.items.some(item => !item.description || item.rate <= 0)) {
      toast.error('Please fill all item details with valid rates');
      return;
    }
    try {
      await axios.post(`${API}/quotations`, formData);
      toast.success('Quotation created successfully');
      setOpen(false);
      setFormData({
        customer_id: '',
        items: [{ description: '', quantity: 1, unit: 'Nos', rate: 0 }],
        validity_days: 30,
        terms_and_conditions: '',
        notes: ''
      });
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  const handleDownloadPDF = async (quotationId) => {
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${quotationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendEmail = async (quotationId) => {
    if (!window.confirm('Send this quotation via email to the customer?')) return;
    try {
      await axios.post(`${API}/quotations/${quotationId}/send`);
      toast.success('Quotation sent successfully');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quotation');
    }
  };

  const handleDelete = async (quotationId) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    try {
      await axios.delete(`${API}/quotations/${quotationId}`);
      toast.success('Quotation deleted');
      fetchQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = q.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totals = calculateTotal();

  // Mobile Quotation Card
  const QuotationCard = ({ quotation }) => (
    <div className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 text-sm">{quotation.quotation_number}</p>
          <p className="text-sm text-slate-600 truncate">{quotation.customer_name}</p>
        </div>
        <Badge className={STATUS_COLORS[quotation.status] || STATUS_COLORS.draft}>
          {quotation.status}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">
          Valid: {quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString('en-IN') : '-'}
        </span>
        <span className="font-semibold text-slate-900">
          ₹{quotation.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <Button size="sm" variant="outline" onClick={() => setViewDialog({ open: true, quotation })} className="flex-1">
          <Eye className="h-4 w-4 mr-1" /> View
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(quotation.quotation_id)} className="flex-1">
          <Download className="h-4 w-4 mr-1" /> PDF
        </Button>
        {quotation.status === 'draft' && (
          <Button size="sm" variant="outline" className="text-blue-600 flex-1" onClick={() => handleSendEmail(quotation.quotation_id)}>
            <Send className="h-4 w-4 mr-1" /> Send
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(quotation.quotation_id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="quotations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">Quotations</h2>
          <p className="text-sm lg:text-base text-slate-600 mt-1">Create and manage customer quotations</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto" data-testid="create-quotation-btn">
              <Plus className="mr-2 h-4 w-4" /> Create Quotation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quotation-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl lg:text-2xl font-semibold text-slate-900">New Quotation</DialogTitle>
              <DialogDescription>Create a quotation for a customer</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Selection */}
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Line Items *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="p-3 border rounded-lg bg-slate-50 space-y-3">
                      {/* Mobile: Stack vertically, Desktop: Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                        <div className="sm:col-span-5">
                          <Label className="text-xs text-slate-500 sm:hidden">Description</Label>
                          <Input
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            data-testid={`item-desc-${index}`}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:col-span-6 sm:grid-cols-6">
                          <div className="col-span-1 sm:col-span-2">
                            <Label className="text-xs text-slate-500 sm:hidden">Qty</Label>
                            <Input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                              min="1"
                            />
                          </div>
                          <div className="col-span-1 sm:col-span-1">
                            <Label className="text-xs text-slate-500 sm:hidden">Unit</Label>
                            <Input
                              placeholder="Unit"
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                            />
                          </div>
                          <div className="col-span-1 sm:col-span-3">
                            <Label className="text-xs text-slate-500 sm:hidden">Rate (₹)</Label>
                            <Input
                              type="number"
                              placeholder="Rate (₹)"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-1 flex justify-end">
                          {formData.items.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(index)} className="text-red-500">
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* Show item total on mobile */}
                      <div className="text-right text-sm text-slate-600 sm:hidden">
                        Amount: ₹{(item.quantity * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-full sm:w-64 space-y-1 text-sm bg-slate-100 p-3 rounded-lg">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>₹ {totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST (18%):</span>
                      <span>₹ {totals.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base border-t pt-1">
                      <span>Total:</span>
                      <span>₹ {totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Validity */}
              <div className="space-y-2">
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  value={formData.validity_days}
                  onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 30 })}
                  min="1"
                  className="w-full sm:w-48"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes for this quotation"
                  rows={2}
                />
              </div>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-quotation-btn">
                Create Quotation
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search quotations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quotations List */}
      <Card className="border-slate-200" data-testid="quotations-table-card">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-3">
          <CardTitle className="text-lg lg:text-xl font-semibold text-slate-900">All Quotations</CardTitle>
          <CardDescription>{filteredQuotations.length} quotations found</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center text-slate-500 py-12">Loading...</div>
          ) : filteredQuotations.length === 0 ? (
            <div className="text-center text-slate-500 py-12">
              <FileText className="mx-auto h-12 w-12 text-slate-300 mb-2" />
              <p>No quotations found. Create your first quotation to get started.</p>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="lg:hidden p-4 space-y-3">
                {filteredQuotations.map((quotation) => (
                  <QuotationCard key={quotation.quotation_id} quotation={quotation} />
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Quotation #</TableHead>
                      <TableHead className="font-semibold text-slate-700">Customer</TableHead>
                      <TableHead className="font-semibold text-slate-700">Amount</TableHead>
                      <TableHead className="font-semibold text-slate-700">Valid Until</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotations.map((quotation) => (
                      <TableRow key={quotation.quotation_id} className="hover:bg-slate-50/50" data-testid={`quotation-row-${quotation.quotation_number}`}>
                        <TableCell className="font-medium text-slate-900">{quotation.quotation_number}</TableCell>
                        <TableCell className="text-slate-600">{quotation.customer_name}</TableCell>
                        <TableCell className="font-medium">₹ {quotation.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-slate-600">
                          {quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString('en-IN') : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[quotation.status] || STATUS_COLORS.draft}>
                            {quotation.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewDialog({ open: true, quotation })}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadPDF(quotation.quotation_id)}
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {quotation.status === 'draft' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendEmail(quotation.quotation_id)}
                                title="Send Email"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(quotation.quotation_id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Quotation Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ ...viewDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quotation Details</DialogTitle>
            <DialogDescription>
              {viewDialog.quotation?.quotation_number}
            </DialogDescription>
          </DialogHeader>
          {viewDialog.quotation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Customer</Label>
                  <p className="font-medium">{viewDialog.quotation.customer_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <Badge className={STATUS_COLORS[viewDialog.quotation.status] || STATUS_COLORS.draft}>
                    {viewDialog.quotation.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-500">Created</Label>
                  <p>{new Date(viewDialog.quotation.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Valid Until</Label>
                  <p>{new Date(viewDialog.quotation.valid_until).toLocaleDateString('en-IN')}</p>
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Items</Label>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[150px]">Description</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead>Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewDialog.quotation.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.quantity} {item.unit}</TableCell>
                          <TableCell>₹ {item.rate?.toLocaleString('en-IN')}</TableCell>
                          <TableCell>₹ {item.amount?.toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="w-full sm:w-64 space-y-1 text-sm bg-slate-100 p-3 rounded-lg">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>₹ {viewDialog.quotation.subtotal?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST (18%):</span>
                    <span>₹ {viewDialog.quotation.gst_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base border-t pt-1">
                    <span>Total:</span>
                    <span>₹ {viewDialog.quotation.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {viewDialog.quotation.sent_to && (
                <div className="text-sm text-slate-600 border-t pt-3">
                  <p>Sent to: {viewDialog.quotation.sent_to}</p>
                  <p>Sent on: {new Date(viewDialog.quotation.sent_at).toLocaleDateString('en-IN')}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => handleDownloadPDF(viewDialog.quotation?.quotation_id)} className="w-full sm:w-auto">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            {viewDialog.quotation?.status === 'draft' && (
              <Button onClick={() => handleSendEmail(viewDialog.quotation?.quotation_id)} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                <Send className="h-4 w-4 mr-2" /> Send to Customer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
